/**
 * Sign in, create an account, and reset a password.
 *
 * One screen with four modes rather than four routes: the states are small,
 * they share a form, and moving between them is the most common thing anyone
 * does here -- someone who mistypes a password wants "forgot" one click away,
 * not a page load.
 *
 * The server's wording is shown verbatim wherever it exists. It is written to
 * avoid saying whether an address has an account, and paraphrasing it in the
 * client is the easiest way to undo that: "that email is already registered"
 * is a membership oracle no matter how politely the server answered.
 */
import { motion } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Brand } from "../components/Brand";
import { Modal } from "../components/Modal";
import { useAsync } from "../hooks/useApi";
import { api } from "../lib/api";

type Mode = "login" | "signup" | "forgot" | "reset" | "verify";

/** Only ever holds an email address. See the comment where it is read. */
const REMEMBERED = "tt.remember.email";

/**
 * Whether a typed password already satisfies a listed rule.
 *
 * Only length can be judged here. The other rules depend on values the client
 * has (the email, the name) or does not (a breach list), and a tick that
 * guesses would be worse than no tick -- it would say "met" and then the
 * server would reject.
 */
function ruleMet(rule: string, password: string, minLength: number): boolean {
  if (rule.includes("characters")) return password.length >= minLength;
  if (rule.includes("capital")) return /\p{Lu}/u.test(password);
  if (rule.includes("symbol")) return /[\p{P}\p{S}]/u.test(password);
  // The rest depend on values the client has only some of (the email, the
  // name) or none of (a breach list). A tick that guessed would say "met"
  // and then the server would reject, which is worse than no tick.
  return false;
}

export function AuthScreen({
  onSignedIn, initialMode = "login", resetToken, verifyToken,
}: {
  onSignedIn: () => void;
  initialMode?: Mode;
  resetToken?: string | null;
  verifyToken?: string | null;
}) {
  const [mode, setMode] = useState<Mode>(initialMode);
  // The address is remembered, the password is not. Anything this app stores
  // client-side is readable by any script that gets injected into the page,
  // which turns one XSS into a stolen password -- the browser's own password
  // manager is the right place for it, and the autoComplete attributes below
  // are what invite it to offer.
  const [email, setEmail] = useState(() => localStorage.getItem(REMEMBERED) ?? "");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [remember, setRemember] = useState(
    () => localStorage.getItem(REMEMBERED) !== null,
  );
  const policy = useAsync((sig) => api.passwordPolicy(sig), []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  // Set when a sign-in fails because the address is unconfirmed, so the way
  // out is offered where the problem appears rather than being something to
  // go looking for.
  const [needsResend, setNeedsResend] = useState(false);
  /** The verification token already sent to the server, so it is sent once. */
  const consumed = useRef<string | null>(null);
  // The address a confirmation or reset mail just went to. Held separately
  // from `email` so editing the field behind the dialog cannot change what
  // the dialog says was sent.
  const [sentTo, setSentTo] = useState<string | null>(null);

  // A confirmation link is consumed on arrival rather than behind a button:
  // the user already clicked something, and asking them to click again to
  // "really" confirm is a step that exists only because it was easier to
  // build.
  useEffect(() => {
    if (mode !== "verify" || !verifyToken) return;
    // Once per token, not once per effect run. A confirmation token is
    // single-use, so a second call necessarily fails -- and React's dev
    // StrictMode runs every effect twice, which meant the first call verified
    // the account and the second reported the link as already used. The
    // account was confirmed and the screen said it was not.
    if (consumed.current === verifyToken) return;
    consumed.current = verifyToken;

    // No cancellation flag here, deliberately. The usual `let alive = true`
    // with `alive = false` in cleanup is wrong for a single-use token under
    // StrictMode: the first run starts the request, its cleanup marks the
    // result stale, and the second run is blocked by the guard above -- so
    // the only request in flight has its answer thrown away and the screen
    // sits on "Confirming…" forever. The guard already guarantees exactly one
    // request, and React 18 does not warn about setting state after unmount.
    setBusy(true);
    api
      .verifyEmail(verifyToken)
      .then((out) => {
        setNotice(out.message ?? "Address confirmed.");
        setMode("login");
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
        setMode("login");
        setNeedsResend(true);
      })
      .finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyToken]);

  /**
   * Clear the messages and move to another mode.
   *
   * Deliberately not an effect on `mode`. Several flows set a message and
   * then switch mode as part of the same action -- sign-up says "check your
   * email" and moves to sign-in -- and an effect keyed on mode wipes that
   * message the moment it lands, leaving a blank form and no explanation for
   * what just happened. Clearing here means only a mode change the *user*
   * asked for discards what the last one said.
   */
  const go = useCallback((next: Mode) => {
    setError(null);
    setNotice(null);
    setWarning(null);
    setNeedsResend(false);
    setMode(next);
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    setWarning(null);
    try {
      if (mode === "signup" && password !== confirm) {
        setError("The passwords do not match.");
        return;
      }
      if (mode === "reset" && password !== confirm) {
        setError("The passwords do not match.");
        return;
      }

      // Written before the request so it survives a failed sign-in: retyping
      // the address after a wrong password is the most annoying possible
      // outcome of getting the password wrong.
      if (remember) localStorage.setItem(REMEMBERED, email);
      else localStorage.removeItem(REMEMBERED);

      if (mode === "login") {
        await api.login(email, password, remember);
        onSignedIn();
      } else if (mode === "signup") {
        const out = await api.register({
          email,
          password,
          confirm_password: confirm,
          first_name: firstName || undefined,
          last_name: lastName || undefined,
          remember,
        });
        // Always a message and never a session now: the account is created
        // unverified, so there is nothing to sign in to yet. A taken address
        // returns the identical body, which is how the server avoids
        // confirming that the address exists -- so this dialog is shown in
        // both cases and says the same thing in both.
        setSentTo(email);
        if (out.warning) setWarning(out.warning);
        setMode("login");
        setPassword("");
        setConfirm("");
      } else if (mode === "forgot") {
        const out = await api.requestReset(email);
        setSentTo(email);
        if (out.warning) setWarning(out.warning);
        setMode("login");
      } else {
        const out = await api.resetPassword(resetToken ?? "", password);
        setNotice(out.message ?? "Password changed.");
        setMode("login");
        setPassword("");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      // The server says this in one place and one way; matching on it here is
      // fragile, so it keys on the status the API client carries instead.
      const status = (err as { status?: number } | null)?.status;
      if (status === 403) setNeedsResend(true);
    } finally {
      setBusy(false);
    }
  };

  const heading = {
    login: "Sign in",
    signup: "Create your account",
    forgot: "Reset your password",
    reset: "Choose a new password",
    verify: "Confirming your address…",
  }[mode];

  const cta = {
    login: "Sign in",
    signup: "Create account",
    forgot: "Send reset link",
    reset: "Change password",
    verify: "",
  }[mode];

  return (
    <div className="auth-screen">
      <div className="auth-brand-hero">
        <Brand size={72} markOnly />
      </div>
      <motion.div
        className="auth-card"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22, ease: [0.3, 0, 0.2, 1] }}
      >
        <h1 className="auth-title">{heading}</h1>

        <form onSubmit={submit} className="auth-form">
          {mode === "signup" && (
            <div className="auth-row">
              <label className="auth-field">
                <span>First name</span>
                <input
                  className="input"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  // given-name / family-name rather than one "name" field, so
                  // a password manager fills each correctly instead of putting
                  // the whole name in the first box.
                  autoComplete="given-name"
                  maxLength={80}
                  autoFocus
                />
              </label>
              <label className="auth-field">
                <span>Last name</span>
                <input
                  className="input"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="family-name"
                  maxLength={80}
                />
              </label>
            </div>
          )}

          {mode !== "reset" && (
            <label className="auth-field">
              <span>Email</span>
              <input
                className="input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                maxLength={320}
                autoFocus={mode !== "signup"}
              />
            </label>
          )}

          {mode !== "forgot" && (
            <label className="auth-field">
              <span>
                Password
                {mode === "login" && (
                  <button type="button" className="auth-inline" onClick={() => go("forgot")}>
                    Forgot?
                  </button>
                )}
              </span>
              <input
                className="input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                // new-password on sign-up and reset so a manager offers to
                // generate one instead of filling the old one back in.
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                maxLength={1024}
                autoFocus={mode === "reset"}
              />
            </label>
          )}

          {(mode === "signup" || mode === "reset") && (
            <label className="auth-field">
              <span>
                Confirm password
                {confirm.length > 0 && (
                  <em className={password === confirm ? "match" : "mismatch"}>
                    {password === confirm ? "matches" : "does not match"}
                  </em>
                )}
              </span>
              <input
                className="input"
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                maxLength={1024}
              />
            </label>
          )}

          {(mode === "signup" || mode === "reset") && policy.data && (
            // Listed from the server's own rules rather than restated here: a
            // form that omits a rule produces a rejection the user was given
            // no way to anticipate.
            <div className="auth-policy">
              <strong>Password requirements</strong>
              <ul>
                {policy.data.rules.map((r) => (
                  <li key={r} className={ruleMet(r, password, policy.data!.min_length)
                    ? "met" : undefined}>
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(mode === "login" || mode === "signup") && (
            <label className="auth-check">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              <span>
                Remember me
                <em>
                  Keeps you signed in for 30 days and fills your email next
                  time. Your password is saved by your browser, not by us.
                </em>
              </span>
            </label>
          )}

          {error && <div className="err">{error}</div>}
          {needsResend && (
            <button
              type="button"
              className="btn small"
              disabled={busy || !email}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  const out = await api.resendVerification(email);
                  setSentTo(email);
                  if (out.warning) setWarning(out.warning);
                  setNeedsResend(false);
                } catch (err) {
                  setError(err instanceof Error ? err.message : String(err));
                } finally {
                  setBusy(false);
                }
              }}
            >
              Send me a new confirmation link
            </button>
          )}
          {notice && <div className="notebox">{notice}</div>}
          {warning && <div className="warnbox">{warning}</div>}

          <button className="btn primary auth-submit" disabled={busy}>
            {busy ? <span className="spinner" /> : null} {cta}
          </button>
        </form>

        <Modal
          open={sentTo !== null}
          onClose={() => setSentTo(null)}
          title="Check your inbox"
          confirmLabel="Back to sign in"
        >
          <p>
            We sent a link to <strong>{sentTo}</strong>.
          </p>
          <p className="muted small">
            Open it to finish, then sign in here. The link works once and
            expires in 48 hours. If it does not arrive in a few minutes, check
            your spam folder.
          </p>
          {warning && <div className="warnbox">{warning}</div>}
        </Modal>

        <div className="auth-switch">
          {mode === "login" && (
            <>New here? <button onClick={() => go("signup")}>Create an account</button></>
          )}
          {mode === "signup" && (
            <>Already have an account? <button onClick={() => go("login")}>Sign in</button></>
          )}
          {(mode === "forgot" || mode === "reset") && (
            <button onClick={() => go("login")}>Back to sign in</button>
          )}
        </div>
      </motion.div>
    </div>
  );
}
